import React, { useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Grid, Typography, TextField, CircularProgress, Tooltip, Button, IconButton } from "@mui/material";

import { Save as SaveIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BackIcon from "@mui/icons-material/ArrowBackOutlined";
import { RuleAction, ExpressionKeyField, RuleTransformation, RuleOperator } from "../../../../utils/constants";

import useRule from "../../../../hooks/super/useRule";
import useAuth from "../../../../hooks/useAuth";

import {
  CollapseAlert,
  LoadingButton,
  SnackbarAlert,
} from "../../../../components/pages/common/styled";
import {
  ActionComponent,
  AndComponent,
  OperatorComponent,
  TransformationComponent,
  VariableComponent,
  ValueComponent,
} from "../../../../components/pages/application/waf/rule/custom/component";
import PageHeader from "../../../../components/pages/_new/super/common/PageHeader";
import OutlinedButton from "../../../../components/pages/_new/super/common/OutlinedButton";
import { TOKENS, SectionCard } from "../../../../components/pages/_new/super/design";

function SAEditCustomRule() {
  const navigate = useNavigate();
  const { customRuleId } = useParams();
  const { isAuthenticated, userType } = useAuth();
  const { getCustomRule, updateCustomRule, setErr, errMsg } = useRule();
  const [ruleName, setRuleName] = React.useState();
  const [conditions, setConditions] = React.useState([
    {
      key: [],
      transform: [],
      operator: RuleOperator.NONE,
      value: "",
    },
  ]);

  const [action, setAction] = React.useState(RuleAction.ALLOW);
  const [initFlag, setInitFlag] = React.useState(false);
  const readOnly = false;

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const init = async () => {
      setInitFlag(true);
      setErr(null);
      const res = await getCustomRule(customRuleId);
      setRuleName(res?.description || undefined);
      const _conditions = res?.conditions;
      _conditions.forEach((_condition) => {
        if (!_condition.transform || !_condition.transform.length) {
          _condition.transform = [RuleTransformation.NONE];
        }
        if (_condition.negative) {
          _condition.operator = "!" + _condition.operator;
        }
      });
      setConditions(
        _conditions || [
          {
            key: ["none"],
            transform: [RuleTransformation.NONE],
            operator: RuleOperator.NONE,
            value: "",
          },
        ]
      );
      setAction(res?.action || RuleAction.ALLOW);
      setInitFlag(false);
    };
    if (isAuthenticated) init();
    return () => setErr(null);
  }, [isAuthenticated, setErr, getCustomRule, customRuleId]);

  const ruleNameChange = (event) => {
    if (readOnly) return;
    setRuleName(event.target.value);
  };

  const selectVariables = useCallback(
    (keys, i) => {
      if (readOnly) return;
      let list = [...conditions];
      if (i < list.length) {
        list[i]["key"] = keys;
      }
      setConditions(list);
    },
    [readOnly, conditions]
  );
  const selectTransforms = useCallback(
    (transforms, i) => {
      if (readOnly) return;
      let list = [...conditions];
      if (i < list.length) {
        list[i]["transform"] = transforms;
      }
      setConditions(list);
    },
    [readOnly, conditions]
  );
  const selectOperatorStr = (event, i) => {
    if (readOnly) return;
    let list = [...conditions];
    if (i < list.length) {
      list[i]["operator"] = event.target.value;
    }
    setConditions(list);
  };
  const selectValueStr = (value, i) => {
    if (readOnly) return;
    let list = [...conditions];
    if (i < list.length) {
      list[i]["value"] = value;
    }
    setConditions(list);
  };
  const duplicateClick = (i) => {
    if (readOnly) return;
    let list = [...conditions];
    if (i < list.length) {
      list.push(JSON.parse(JSON.stringify(list[i])));
    }
    setConditions(list);
  };

  const addClick = (e) => {
    if (readOnly) return;
    let list = [...conditions];
    list.push({
      key: [],
      transform: [],
      operator: RuleOperator.NONE,
      value: "",
    });
    setConditions(list);
  };

  const removeClick = (i) => {
    if (readOnly) return;
    let list = [...conditions];
    if (i < list.length) {
      list.splice(i, 1);
    }
    if (0 === list.length) {
      list.push({
        key: [],
        transform: [],
        operator: RuleOperator.NONE,
        value: "",
      });
    }
    setConditions(list);
  };

  const onBackPressed = (event) => {
    // navigate("/application/" + configSite + "/firewall");
    navigate(-1);
  };

  const [errOpen, setErrOpen] = React.useState(false);
  const [snackOpen, setSnackOpen] = React.useState(false);
  const handleSnackClose = () => {
    setSnackOpen(false);
  };
  const [message, setMessage] = React.useState();
  const [success, setSuccess] = React.useState();
  const [loading, setLoading] = React.useState(false);

  const handleSave = async () => {
    if (readOnly) return;
    let conditionCheck = true;
    const _conditions = JSON.parse(JSON.stringify(conditions)); // Deep copy of conditions
    _conditions.forEach((andCond) => {
      if (
        andCond.key === undefined ||
        andCond.key === null ||
        andCond.key[0] === "none" ||
        andCond.transform === undefined ||
        andCond.transform === null ||
        andCond.operator === "none" ||
        andCond.operator === undefined ||
        andCond.operator === null
      ) {
        conditionCheck = false;
      }

      if (!andCond.value && !(andCond.operator === RuleOperator.DETECT_SQLI || andCond.operator === RuleOperator.DETECT_XSS)) {
        conditionCheck = false;
      }

      if (-1 < andCond.operator.indexOf("!")) {
        andCond.operator = andCond.operator.substring(1);
        andCond.negative = true;
      } else {
        andCond.negative = false;
      }
      if (1 === andCond.transform.length && RuleTransformation.NONE === andCond.transform[0]) {
        andCond.transform = [];
      }
      if (RuleOperator.DETECT_SQLI === andCond.operator || RuleOperator.DETECT_XSS === andCond.operator) {
        andCond.value = "";
      }
    });
    if (conditionCheck === false) {
      setErr("You must select or input the field, operator, and value for request matching.");
      return;
    }
    setLoading(true);
    const result = await updateCustomRule(customRuleId, {
      description: ruleName,
      conditions: _conditions,
      action: action,
    });
    setMessage(result.msg);
    setSuccess(result.status);
    setLoading(false);
    setSnackOpen(true);
    if ("success" === result.status) {
      setTimeout(() => {
        onBackPressed(null);
      }, 1000);
    }
  };
  React.useEffect(() => {
    setErrOpen(true);
  }, [errMsg]);

  return (
    <React.Fragment>
      <Helmet title={readOnly ? "View Custom Rule" : "Edit Custom Rule"} />
      <PageHeader
        title={readOnly ? "View Custom Rule" : "Edit Custom Rule"}
        description="Update conditions and resulting action"
        actions={
          <>
            <OutlinedButton onClick={onBackPressed} startIcon={<BackIcon style={{ width: 18, height: 18 }} />}>
              Back
            </OutlinedButton>
            {readOnly ? null : (
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                loadingPosition="start"
                loading={loading}
                onClick={handleSave}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: `${TOKENS.radiusXs}px`,
                  px: 2,
                }}
              >
                Save
              </LoadingButton>
            )}
          </>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      {initFlag ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 2 }}>
          <SectionCard title="Rule Basics">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.fg2, mb: 0.5 }}>
                  Rule Name
                </Typography>
                <TextField value={ruleName} onChange={ruleNameChange} fullWidth size="small" />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            title="When incoming requests match..."
            description="Conditions are AND'd together — add more for stricter matching."
          >
            {conditions.map((andCond, i) => (
              <React.Fragment key={i}>
                <Box
                  sx={{
                    background: TOKENS.bgSunken,
                    border: `1px solid ${TOKENS.borderSubtle}`,
                    borderLeft: `4px solid ${TOKENS.primary}`,
                    borderRadius: `${TOKENS.radiusS}px`,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Grid container alignItems="center">
                    <Grid item xs>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1 }}>
                        Field
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Tooltip title="Duplicate condition">
                        <IconButton size="small" onClick={() => duplicateClick(i)} sx={{ color: TOKENS.fg2 }}>
                          <AddCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove condition">
                        <IconButton size="small" onClick={() => removeClick(i)} sx={{ color: TOKENS.fg2 }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 1 }}>
                    <VariableComponent variables={andCond.key} selectVariables={selectVariables} padx={i} />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1, mb: 0.5 }}>
                      Transformation
                    </Typography>
                    <TransformationComponent
                      transforms={andCond.transform}
                      selectTransforms={selectTransforms}
                      disabled={andCond.key?.length === 0}
                      padx={i}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1, mb: 0.5 }}>
                      Operator
                    </Typography>
                    <OperatorComponent
                      operatorStr={andCond.operator}
                      selectOperatorStr={(event) => selectOperatorStr(event, i)}
                      disabled={andCond.key?.length === 0}
                      padx={i}
                    />
                  </Box>
                  {andCond.operator === RuleOperator.DETECT_SQLI ||
                  andCond.operator === RuleOperator.DETECT_XSS ? null : (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1, mb: 0.5 }}>
                        Value
                      </Typography>
                      <ValueComponent
                        valueStr={andCond.value}
                        selectValueStr={(event) => selectValueStr(event, i)}
                        disabled={andCond.key === ExpressionKeyField.NONE || andCond.operator === "none"}
                        padx={i}
                      />
                    </Box>
                  )}
                </Box>
                {conditions.length - 1 > i && <AndComponent />}
              </React.Fragment>
            ))}
            <Button
              variant="outlined"
              onClick={addClick}
              startIcon={<AddCircleOutlineIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: TOKENS.fg1,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                "&:hover": { background: TOKENS.bgSunken },
              }}
            >
              Add Condition
            </Button>
          </SectionCard>

          <SectionCard title="Then" description="Choose what to do with matching requests">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <ActionComponent userType={userType} action={action} setAction={setAction} />
              </Grid>
            </Grid>
          </SectionCard>
        </Box>
      )}

      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}
export default SAEditCustomRule;
